import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {Datepicker} from "@mobiscroll/react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {alpha} from "@mui/material/styles";
import {formatFullDMY} from "crqlar-react-common/dist/utils/date.util";
import {useId, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    NEW_DATE_PICKER_POPUP_CLASS,
    DATE_PICKER_SIZE_TOKENS,
    type DateRange,
    toPickerDate,
    useCalendarLocale,
    useMobiscrollThemeStyles,
} from "../../../common/newDatePicker/datePickerShared";
import {
    createDefaultInsightsRange,
    insightsRangeForPreset,
    MAX_RELATIVE_DAYS,
    MIN_RELATIVE_DAYS,
    presetForRange,
    RELATIVE_DATE_RANGE_PRESETS,
    relativeComparisonRange,
    relativeDays,
    relativeRangeForPreset,
    type InsightsDateRangePreset,
    type RelativeComparison,
    type RelativeDateRangePreset,
    type RelativeDateRangeSelection,
} from "../insightsDateRangePresets";
import "@mobiscroll/react/dist/css/mobiscroll.scss";

type DateRangeMenuPreset = InsightsDateRangePreset | "custom";
type ComparisonPreset = "previousPeriod" | "previousYear" | "custom";
type ActiveDraftRange = "current" | "comparison";
type PickerChangeEvent = {value?: unknown};

function samePeriodLastYear({start, end}: DateRange): DateRange {
    const previousStart = new Date(start);
    previousStart.setFullYear(previousStart.getFullYear() - 1);
    const previousEnd = new Date(end);
    previousEnd.setFullYear(previousEnd.getFullYear() - 1);
    return {start: previousStart, end: previousEnd};
}

function previousPeriod({start, end}: DateRange): DateRange {
    const rangeLength = Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1;
    const previousEnd = new Date(start);
    previousEnd.setDate(previousEnd.getDate() - 1);
    const previousStart = new Date(previousEnd);
    previousStart.setDate(previousStart.getDate() - (rangeLength - 1));
    return {start: previousStart, end: previousEnd};
}

function comparisonRangeFor(preset: Exclude<ComparisonPreset, "custom">, range: DateRange): DateRange {
    return preset === "previousPeriod" ? previousPeriod(range) : samePeriodLastYear(range);
}

export interface InsightsDateRangeSelection {
    range: DateRange;
    compareRange: DateRange | null;
}

interface InsightsDateRangeControlProps extends InsightsDateRangeSelection {
    onChange: (selection: InsightsDateRangeSelection) => void;

    /** Whether the menu offers a compare range at all. A worklist has nothing to compare. */
    comparison?: boolean;

    /**
     * Whether the menu offers "All time", a real range from a fixed floor to today. Meant
     * for lists, where the range is a filter and "everything" is a legitimate answer.
     */
    allTime?: boolean;

    relative?: undefined;
}

/**
 * Relative mode, for a period recomputed on every run (a scheduled report): rolling presets that end before
 * `referenceDay`, a comparison with the previous period or year, and a read-only calendar of the days they cover.
 * It emits the preset, never dates.
 */
interface RelativeDateRangeControlProps {
    relative: RelativeDateRangeSelection;
    onRelativeChange: (selection: RelativeDateRangeSelection) => void;
    /** The day the period counts back from, e.g. the next run's in the organization's calendar. Default: today. */
    referenceDay?: Date;
    disabled?: boolean;
}

const isRelativeMode = (
    props: InsightsDateRangeControlProps | RelativeDateRangeControlProps,
): props is RelativeDateRangeControlProps => Boolean(props.relative);

/** The days a relative selection covers for a run on `referenceDay`; no range for "lastNDays" without valid days. */
const relativeRanges = ({preset, days, comparison}: RelativeDateRangeSelection, referenceDay?: Date) => {
    const range = relativeRangeForPreset(preset, days, referenceDay);
    return {range, compareRange: range && comparison ? relativeComparisonRange(preset, range, comparison) : null};
};

export function InsightsDateRangeControl(props: InsightsDateRangeControlProps | RelativeDateRangeControlProps) {
    const relative = isRelativeMode(props) ? props : null;
    const absolute = isRelativeMode(props) ? null : props;
    const comparison = absolute ? absolute.comparison ?? true : true;
    const allTime = absolute?.allTime ?? false;
    const {range, compareRange}: {range: DateRange | null; compareRange: DateRange | null} = isRelativeMode(props)
        ? relativeRanges(props.relative, props.referenceDay)
        : props;
    const {t} = useTranslation();
    const {calendarLocale} = useCalendarLocale();
    const accent = useMobiscrollThemeStyles();
    const triggerSize = DATE_PICKER_SIZE_TOKENS.medium;
    const rangePreset = absolute ? presetForRange(absolute.range) : null;
    const [comparisonPreset, setComparisonPreset] = useState<ComparisonPreset>("previousYear");
    const [draftRange, setDraftRange] = useState<DateRange>(createDefaultInsightsRange);
    const [draftCompareRange, setDraftCompareRange] = useState<DateRange>(() => (
        samePeriodLastYear(createDefaultInsightsRange())
    ));
    const [draftRangePreset, setDraftRangePreset] = useState<DateRangeMenuPreset>("last30Days");
    const [draftComparisonPreset, setDraftComparisonPreset] = useState<ComparisonPreset>("previousYear");
    const [draftComparisonEnabled, setDraftComparisonEnabled] = useState(false);
    const [activeDraftRange, setActiveDraftRange] = useState<ActiveDraftRange>("current");
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [draftRelativePreset, setDraftRelativePreset] = useState<RelativeDateRangePreset>("last7Days");
    const [draftDays, setDraftDays] = useState("");
    const daysFieldId = useId();

    const open = (nextAnchorEl: HTMLElement, initialActiveRange: ActiveDraftRange = "current") => {
        if (relative) {
            setDraftRelativePreset(relative.relative.preset);
            setDraftDays(relative.relative.days === undefined ? "" : String(relative.relative.days));
            setDraftComparisonPreset(relative.relative.comparison ?? "previousPeriod");
            setDraftComparisonEnabled(relative.relative.comparison !== null);
        } else if (absolute) {
            setDraftRange(absolute.range);
            setDraftCompareRange(absolute.compareRange ?? samePeriodLastYear(absolute.range));
            setDraftRangePreset(rangePreset ?? "custom");
            setDraftComparisonPreset(comparisonPreset);
            setDraftComparisonEnabled(comparison && Boolean(absolute.compareRange));
        }
        setActiveDraftRange(initialActiveRange);
        setAnchorEl(nextAnchorEl);
    };

    const selectRelativePreset = (preset: RelativeDateRangePreset) => {
        setDraftRelativePreset(preset);
        setActiveDraftRange("current");
    };

    const selectRangePreset = (preset: DateRangeMenuPreset) => {
        setDraftRangePreset(preset);
        setActiveDraftRange("current");
        if (preset !== "custom") {
            const nextRange = insightsRangeForPreset(preset);
            setDraftRange(nextRange);
            if (draftComparisonEnabled && draftComparisonPreset !== "custom") {
                setDraftCompareRange(comparisonRangeFor(draftComparisonPreset, nextRange));
            }
        }
    };

    const selectComparisonPreset = (preset: ComparisonPreset) => {
        setDraftComparisonPreset(preset);
        setActiveDraftRange("comparison");
        if (preset !== "custom") {
            setDraftCompareRange(comparisonRangeFor(preset, draftRange));
        }
    };

    const handleDraftRangeChange = ({value: raw}: PickerChangeEvent) => {
        const rawValues = Array.isArray(raw) ? raw : [raw, raw];
        const start = toPickerDate(rawValues[0]);
        const end = toPickerDate(rawValues[1] ?? rawValues[0]) ?? start;

        if (!start || !end) {
            return;
        }

        if (activeDraftRange === "comparison") {
            setDraftCompareRange({start, end});
            setDraftComparisonPreset("custom");
        } else {
            const nextRange = {start, end};
            setDraftRange(nextRange);
            setDraftRangePreset("custom");
            if (draftComparisonEnabled && draftComparisonPreset !== "custom") {
                setDraftCompareRange(comparisonRangeFor(draftComparisonPreset, nextRange));
            }
        }
    };

    const toggleDraftComparison = (enabled: boolean) => {
        setDraftComparisonEnabled(enabled);
        if (enabled) {
            if (draftComparisonPreset !== "custom") {
                setDraftCompareRange(comparisonRangeFor(draftComparisonPreset, draftRange));
            }
            setActiveDraftRange("comparison");
        } else {
            setActiveDraftRange("current");
        }
    };

    const draftRelativeComparison: RelativeComparison | null = draftComparisonEnabled
        ? (draftComparisonPreset === "previousYear" ? "previousYear" : "previousPeriod")
        : null;
    const relativeDraft = relative
        ? relativeRanges(
            {preset: draftRelativePreset, days: relativeDays(draftDays), comparison: draftRelativeComparison},
            relative.referenceDay,
        )
        : null;
    const relativeDraftIncomplete = Boolean(relativeDraft) && !relativeDraft?.range;

    const apply = () => {
        if (relative) {
            relative.onRelativeChange({
                preset: draftRelativePreset,
                days: draftRelativePreset === "lastNDays" ? relativeDays(draftDays) : undefined,
                comparison: draftRelativeComparison,
            });
        } else if (absolute) {
            setComparisonPreset(draftComparisonPreset);
            absolute.onChange({
                range: draftRange,
                compareRange: comparison && draftComparisonEnabled ? draftCompareRange : null,
            });
        }
        setAnchorEl(null);
    };

    const removeComparison = () => {
        if (relative) relative.onRelativeChange({...relative.relative, comparison: null});
        else if (absolute) absolute.onChange({range: absolute.range, compareRange: null});
    };

    const formatRange = (value: DateRange) => `${formatFullDMY(value.start)} – ${formatFullDMY(value.end)}`;

    const relativePresetLabels: Record<RelativeDateRangePreset, string> = {
        yesterday: t("Yesterday"),
        lastWeek: t("Last week"),
        last7Days: t("Last 7 days"),
        last30Days: t("Last 30 days"),
        lastMonth: t("Last month"),
        lastQuarter: t("Last quarter"),
        lastNDays: t("Custom number of days"),
    };

    const relativeName = ({preset, days}: RelativeDateRangeSelection) => {
        const count = preset === "lastNDays" ? relativeDays(days) : undefined;
        if (count === undefined) return relativePresetLabels[preset];
        return count === 1 ? t("Yesterday") : t("Last {{count}} days", {count});
    };

    // "All time" is the one preset whose dates say less than its name; a relative period leads with its name.
    const triggerLabel = (value: DateRange | null, activeRange: ActiveDraftRange) => {
        if (relative) {
            const name = activeRange === "current"
                ? relativeName(relative.relative)
                : relative.relative.comparison === "previousYear" ? t("Previous year") : t("Previous period");
            return value ? `${name} · ${formatRange(value)}` : name;
        }
        if (!value) return "";
        return activeRange === "current" && rangePreset === "allTime" ? t("All time") : formatRange(value);
    };

    const rangePresets: [DateRangeMenuPreset, string][] = [
        ["today", t("Today")],
        ["yesterday", t("Yesterday")],
        ["thisWeek", t("This week")],
        ["last7Days", t("Last 7 days")],
        ["lastWeek", t("Last week")],
        ["thisMonth", t("This month")],
        ["last30Days", t("Last 30 days")],
        ["thisYear", t("This year")],
        ...(allTime ? [["allTime", t("All time")] as [DateRangeMenuPreset, string]] : []),
    ];
    const menuPresets: {key: string; label: string; selected: boolean; select: () => void}[] = relative
        ? RELATIVE_DATE_RANGE_PRESETS.map(preset => ({
            key: preset,
            label: relativePresetLabels[preset],
            selected: draftRelativePreset === preset,
            select: () => selectRelativePreset(preset),
        }))
        : rangePresets.map(([preset, label]) => ({
            key: preset,
            label,
            selected: draftRangePreset === preset,
            select: () => selectRangePreset(preset),
        }));
    // Relative mode offers no custom comparison: its days move with every run.
    const comparisonPresets: [ComparisonPreset, string][] = [
        ["previousPeriod", t("Previous period")],
        ["previousYear", t("Previous year")],
        ...(relative ? [] : [["custom", t("Custom")] as [ComparisonPreset, string]]),
    ];
    const shownDraftRange: DateRange | null = relativeDraft ? relativeDraft.range : draftRange;
    const shownDraftCompareRange: DateRange | null = relativeDraft ? relativeDraft.compareRange : draftCompareRange;
    const activeDraftRangeValue = activeDraftRange === "comparison" ? shownDraftCompareRange : shownDraftRange;
    const renderRangeTrigger = (
        value: DateRange | null,
        activeRange: ActiveDraftRange,
        onRemove?: () => void,
    ) => (
        <Box
            sx={theme => ({
                height: triggerSize.height,
                display: "flex",
                alignItems: "center",
                boxSizing: "border-box",
                border: "1px solid",
                borderColor: "rgba(0, 0, 0, 0.23)",
                borderRadius: {xs: 1, md: 2},
                bgcolor: activeRange === "comparison"
                    ? alpha(theme.palette.info.main, 0.08)
                    : "background.paper",
                color: activeRange === "comparison" ? theme.palette.info.dark : theme.palette.text.primary,
                overflow: "hidden",
                cursor: "default",
                "&:hover": {
                    borderColor: "text.primary",
                },
                "&:focus-within": {
                    borderColor: "primary.main",
                    boxShadow: `inset 0 0 0 1px ${accent}`,
                },
            })}
        >
            <ButtonBase
                aria-haspopup="dialog"
                aria-expanded={Boolean(anchorEl) && activeDraftRange === activeRange}
                aria-controls={anchorEl ? "insights-v2-date-picker" : undefined}
                disabled={relative?.disabled}
                onClick={event => open(event.currentTarget, activeRange)}
                sx={{
                    alignSelf: "stretch",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,
                    px: 1.5,
                    color: "inherit",
                    textAlign: "left",
                }}
            >
                <CalendarMonthOutlinedIcon sx={{fontSize: triggerSize.todayIcon, flexShrink: 0}}/>
                <Typography
                    component="span"
                    noWrap
                    sx={{
                        minWidth: 0,
                        fontSize: triggerSize.fontSize,
                        fontWeight: 500,
                        lineHeight: triggerSize.lineHeight,
                        mt: triggerSize.textNudge,
                    }}
                >
                    {triggerLabel(value, activeRange)}
                </Typography>
            </ButtonBase>

            {onRemove && (
                <IconButton
                    size="small"
                    aria-label={t("Remove comparison")}
                    disabled={relative?.disabled}
                    onClick={onRemove}
                    sx={theme => ({
                        mr: 0.5,
                        color: "inherit",
                        "&:hover": {bgcolor: alpha(theme.palette.info.main, 0.12)},
                    })}
                >
                    <CloseIcon fontSize="small"/>
                </IconButton>
            )}
        </Box>
    );

    return (
        <Box sx={{display: "flex", alignItems: "center", gap: 1, minWidth: 0, flexWrap: "wrap"}}>
            {renderRangeTrigger(range, "current")}

            {compareRange && (
                <>
                    <Typography variant="body2" color="text.secondary">
                        {t("vs")}
                    </Typography>

                    {renderRangeTrigger(compareRange, "comparison", removeComparison)}
                </>
            )}

            <Popover
                id="insights-v2-date-picker"
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                transformOrigin={{vertical: "top", horizontal: "left"}}
                PaperProps={{
                    role: "dialog",
                    "aria-label": t("Select date range"),
                    sx: {
                        width: {xs: "calc(100vw - 32px)", sm: 560},
                        maxWidth: "calc(100vw - 32px)",
                        maxHeight: "calc(100dvh - 96px)",
                        overflowY: "auto",
                        mt: 1,
                        p: 0,
                        borderRadius: 2,
                    },
                }}
            >
                <Box sx={{display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr)", sm: "176px minmax(0, 1fr)"}}}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            minWidth: 0,
                            borderColor: "divider",
                        }}
                    >
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {xs: "repeat(2, minmax(0, 1fr))", sm: "1fr"},
                                py: 1,
                            }}
                        >
                            {menuPresets.map(({key, label, selected, select}) => (
                                <Button
                                    key={key}
                                    color="inherit"
                                    onClick={select}
                                    sx={{
                                        justifyContent: "flex-start",
                                        minHeight: 40,
                                        px: 2,
                                        borderRadius: 0,
                                        fontWeight: selected ? 700 : 400,
                                        bgcolor: selected ? "action.selected" : "transparent",
                                        textTransform: "none",
                                    }}
                                >
                                    {label}
                                </Button>
                            ))}
                        </Box>

                        {comparison && (
                            <>
                                <Divider/>

                                <FormControlLabel
                                    control={(
                                        <Switch
                                            size="small"
                                            checked={draftComparisonEnabled}
                                            onChange={event => toggleDraftComparison(event.target.checked)}
                                        />
                                    )}
                                    label={t("Compare")}
                                    labelPlacement="start"
                                    sx={{minHeight: 48, justifyContent: "space-between", gap: 1, mx: 0, px: 2}}
                                />
                            </>
                        )}

                        {comparison && draftComparisonEnabled && (
                            <Box sx={{pb: 1}}>
                                {comparisonPresets.map(([preset, label]) => (
                                    <Button
                                        key={preset}
                                        color="inherit"
                                        onClick={() => selectComparisonPreset(preset)}
                                        sx={theme => ({
                                            justifyContent: "flex-start",
                                            width: "100%",
                                            minHeight: 40,
                                            px: 2,
                                            borderRadius: 0,
                                            fontWeight: draftComparisonPreset === preset ? 700 : 400,
                                            color: draftComparisonPreset === preset
                                                ? theme.palette.info.dark
                                                : theme.palette.text.primary,
                                            bgcolor: draftComparisonPreset === preset
                                                ? alpha(theme.palette.info.main, 0.12)
                                                : "transparent",
                                            textTransform: "none",
                                        })}
                                    >
                                        {label}
                                    </Button>
                                ))}
                            </Box>
                        )}
                    </Box>

                    <Stack spacing={2} sx={{minWidth: 0, p: 2}}>
                        {relative && draftRelativePreset === "lastNDays" && (
                            <TextField
                                id={daysFieldId}
                                size="small"
                                type="number"
                                label={t("Days")}
                                value={draftDays}
                                onChange={event => setDraftDays(event.target.value)}
                                error={relativeDraftIncomplete && draftDays.trim() !== ""}
                                inputProps={{min: MIN_RELATIVE_DAYS, max: MAX_RELATIVE_DAYS, step: 1}}
                                InputLabelProps={{shrink: true}}
                            />
                        )}

                        <Stack spacing={1}>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<CalendarMonthOutlinedIcon/>}
                                onClick={() => setActiveDraftRange("current")}
                                sx={{
                                    justifyContent: "flex-start",
                                    minHeight: 56,
                                    px: 1.5,
                                    borderRadius: 2,
                                    borderColor: "divider",
                                    color: "text.primary",
                                    bgcolor: activeDraftRange === "current" ? "action.selected" : "transparent",
                                    textTransform: "none",
                                    "&:hover": {borderColor: "text.disabled"},
                                }}
                            >
                                <Stack alignItems="flex-start" spacing={0}>
                                    <Typography variant="caption" color="text.secondary">
                                        {t("Current")}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {shownDraftRange ? formatRange(shownDraftRange) : "–"}
                                    </Typography>
                                </Stack>
                            </Button>

                            {comparison && draftComparisonEnabled && (
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<CalendarMonthOutlinedIcon/>}
                                    onClick={() => setActiveDraftRange("comparison")}
                                    sx={theme => ({
                                        justifyContent: "flex-start",
                                        minHeight: 56,
                                        px: 1.5,
                                        borderRadius: 2,
                                        borderColor: theme.palette.divider,
                                        color: theme.palette.text.primary,
                                        bgcolor: activeDraftRange === "comparison"
                                            ? alpha(theme.palette.info.main, 0.08)
                                            : "transparent",
                                        textTransform: "none",
                                        "&:hover": {
                                            borderColor: theme.palette.text.disabled,
                                            bgcolor: alpha(theme.palette.info.main, 0.08),
                                        },
                                    })}
                                >
                                    <Stack alignItems="flex-start" spacing={0}>
                                        <Typography variant="caption" color="info.dark">
                                            {t("Compare to")}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            {shownDraftCompareRange ? formatRange(shownDraftCompareRange) : "–"}
                                        </Typography>
                                    </Stack>
                                </Button>
                            )}
                        </Stack>

                        <Box
                            sx={{
                                minWidth: 0,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                overflow: "hidden",
                                "& .mbsc-datepicker-inline": {width: "100%", border: 0},
                                "& .mbsc-calendar": {width: "100%", border: 0},
                                // Relative mode previews the days; the month can still be paged, a day not picked.
                                ...(relative ? {"& .mbsc-calendar-day": {pointerEvents: "none"}} : {}),
                            }}
                        >
                            <Datepicker
                                key={activeDraftRange}
                                value={activeDraftRangeValue ? [activeDraftRangeValue.start, activeDraftRangeValue.end] : null}
                                onChange={relative ? undefined : handleDraftRangeChange}
                                locale={calendarLocale}
                                cssClass={NEW_DATE_PICKER_POPUP_CLASS}
                                theme="material"
                                themeVariant="light"
                                controls={["calendar"]}
                                firstDay={1}
                                select="range"
                                display="inline"
                                showInput={false}
                                showRangeLabels={false}
                            />
                        </Box>

                        {relative && (
                            <Typography variant="body2" color="text.secondary">
                                {t("The period moves with every run. The calendar shows the days the next run covers.")}
                            </Typography>
                        )}
                    </Stack>
                </Box>

                <Divider/>

                <Stack direction="row" justifyContent="flex-end" gap={1} sx={{p: 1.5}}>
                    <Button color="inherit" onClick={() => setAnchorEl(null)} sx={{textTransform: "none"}}>
                        {t("Cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={apply}
                        disabled={relativeDraftIncomplete}
                        sx={{minWidth: 96, textTransform: "none"}}
                    >
                        {t("Apply")}
                    </Button>
                </Stack>
            </Popover>
        </Box>
    );
}

export default InsightsDateRangeControl;
